import { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Users, ArrowRight } from 'lucide-react';
import api from '@/lib/api';
import { toast } from 'sonner';

export default function GuestJoinPage() {
  const { shareToken } = useParams();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [joining, setJoining] = useState(false);
  const [mapInfo, setMapInfo] = useState(null);
  const [error, setError] = useState(null);
  const [username, setUsername] = useState('');
  const [email, setEmail] = useState('');

  useEffect(() => {
    const fetchMapInfo = async () => {
      try {
        const res = await api.get(`/collab/join/${shareToken}`);
        setMapInfo(res.data);
      } catch (err) {
        if (err.response?.status === 410) {
          setError('This share link has expired');
        } else if (err.response?.status === 404) {
          setError('Invalid share link');
        } else {
          setError('Something went wrong');
        }
      } finally {
        setLoading(false);
      }
    };
    fetchMapInfo();
  }, [shareToken]);

  const handleJoin = async (e) => {
    e.preventDefault();
    if (!username.trim() || !email.trim()) {
      toast.error('Please fill in all fields');
      return;
    }

    setJoining(true);
    try {
      const res = await api.post('/collab/guest-join', {
        share_token: shareToken,
        username: username.trim(),
        email: email.trim()
      });
      
      // Store session in localStorage
      localStorage.setItem('collab_session', JSON.stringify({
        session_token: res.data.session_token,
        guest_id: res.data.guest_id,
        username: res.data.username,
        cursor_color: res.data.cursor_color,
        map_id: res.data.map_id
      }));
      
      // Navigate to collab canvas
      navigate(`/collab/canvas/${res.data.map_id}`);
    } catch (err) {
      toast.error('Failed to join. Please try again.');
    } finally {
      setJoining(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-[#FAFAFA] flex items-center justify-center">
        <div className="w-6 h-6 border-2 border-gray-300 border-t-gray-900 rounded-full animate-spin" />
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-[#FAFAFA] flex items-center justify-center p-4">
        <div className="max-w-sm w-full text-center">
          <div className="w-16 h-16 rounded-full bg-red-100 flex items-center justify-center mx-auto mb-4">
            <span className="text-2xl">😕</span>
          </div>
          <h1 className="font-heading text-xl font-medium text-gray-900 mb-2">{error}</h1>
          <p className="text-sm text-gray-500 mb-6">
            Ask the map owner for a new link.
          </p>
          <Button variant="outline" onClick={() => navigate('/')}>
            Go to Homepage
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#FAFAFA] flex items-center justify-center p-4">
      <div className="w-full max-w-sm">
        {/* Card */}
        <div className="bg-white rounded-2xl shadow-sm border border-gray-200/60 overflow-hidden">
          {/* Header */}
          <div className="px-6 pt-6 pb-4 border-b border-gray-100">
            <div className="flex items-center gap-3 mb-3">
              <div className="w-10 h-10 rounded-xl bg-gray-900 flex items-center justify-center">
                <Users className="w-5 h-5 text-white" strokeWidth={1.5} />
              </div>
              <div>
                <p className="text-xs text-gray-400 uppercase tracking-wide font-medium">Join Session</p>
                <h1 className="font-heading text-lg font-medium text-gray-900 leading-tight">
                  {mapInfo?.map_title}
                </h1>
              </div>
            </div>
          </div>

          {/* Form */}
          <form onSubmit={handleJoin} className="p-6 space-y-4">
            <div>
              <label className="block text-xs font-medium text-gray-600 mb-1.5">
                Your name
              </label>
              <input
                type="text"
                value={username}
                onChange={(e) => setUsername(e.target.value)}
                placeholder="How should we call you?"
                className="w-full px-3 py-2.5 bg-gray-50 border border-gray-200 rounded-lg text-sm
                         placeholder:text-gray-400 focus:outline-none focus:ring-2 focus:ring-gray-900/10 focus:border-gray-300
                         transition-all"
                autoFocus
              />
            </div>

            <div>
              <label className="block text-xs font-medium text-gray-600 mb-1.5">
                Email
              </label>
              <input
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="your@email.com"
                className="w-full px-3 py-2.5 bg-gray-50 border border-gray-200 rounded-lg text-sm
                         placeholder:text-gray-400 focus:outline-none focus:ring-2 focus:ring-gray-900/10 focus:border-gray-300
                         transition-all"
              />
            </div>

            <Button
              type="submit"
              disabled={joining || !username.trim() || !email.trim()}
              className="w-full bg-gray-900 hover:bg-gray-800 text-white h-11 mt-2"
            >
              {joining ? (
                <span className="flex items-center gap-2">
                  <div className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                  Joining...
                </span>
              ) : (
                <span className="flex items-center gap-2">
                  Join Session
                  <ArrowRight className="w-4 h-4" />
                </span>
              )}
            </Button>
          </form>

          {/* Footer */}
          <div className="px-6 pb-6">
            <p className="text-xs text-gray-400 text-center">
              You'll join as a guest with a unique cursor color
            </p>
          </div>
        </div>

        {/* Branding */}
        <p className="text-xs text-gray-400 text-center mt-4">
          Powered by Atlas Mind Mapping
        </p>
      </div>
    </div>
  );
}
